import * as React from 'react';
import styles from './style.module.scss';
import SvgEditService from '../../../services/svg-edit-service';
import ZenDataService from '../../../services/zen-data-service';
import PanelsService from '../../../services/panels-service';
import ColoursService from '../../../services/colours-service';
import { startDownloadBase64EncodedContent } from '../../../common/pdUtils';

interface IPreviewData {
    pdf: string;
    data: IPanelStateData;
}

interface IPanelStateData {
    errors: string[];
    artworkType: string;
}

interface IPreviewDataDTO {
    previewData: IPreviewData[];
}

interface State {
    open: boolean;
    data: IPreviewDataDTO | null;
    errorString: string | null;
    selectedPreviewIndex: number;
    previewTRNumber: string;
    previewPanelType: string;
}

const getDefaultState = (): State => {
    const s: State = {
        open: false,
        data: null,
        errorString: null,
        selectedPreviewIndex: 0,
        previewTRNumber: "",
        previewPanelType: "generic",
    };

    return s;
}

export class PreviewPanelDialog extends React.Component<{}, State> {
    state = getDefaultState();

    private svgEditService = SvgEditService.Get(this);
    private zenDataService = ZenDataService.Get(this);
    private panelService = PanelsService.Get(this);
    private colourService = ColoursService.Get(this);

    public componentWillUnmount() {
        this.svgEditService.UnregisterComponent(this);
        this.zenDataService.UnregisterComponent(this);
        this.panelService.UnregisterComponent(this);
        this.colourService.UnregisterComponent(this);
    }

    public openDialog = async (previewTRNumber: string, previewPanelType: string) => {
        previewTRNumber = previewTRNumber.trim().toUpperCase();

        this.setState({
            open: true,
            data: null,
            errorString: null,
            previewTRNumber,
            previewPanelType,
        });

        await this.colourService.SaveSpotColours();

        const response = await fetch(
            `/api/designer/preview`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    WorkflowJobReference: previewTRNumber,
                    PanelType: previewPanelType,
                    DesignerMode: this.zenDataService.GetDesignerMode(),
                    SVG: this.svgEditService.canvas.svgCanvasToString()
                })
            }
        );

        if (!response.ok) {
            console.error(response);
            this.setState({ errorString: `Could not fetch panel preview. A HTTP ${response.status} (${response.statusText}) error occured!` });
            return;
        }

        let previewData = await response.json() as IPreviewDataDTO;

        if (!previewData) {
            this.setState({ errorString: `Could not fetch panel preview. Response returned no data.` });
            return;
        }

        this.setState({
            data: previewData,
            selectedPreviewIndex: 0,
        });
    }


    public closeDialog = () => {
        this.setState({ open: false });
    }

    public downloadCurrentPDF = () => {
        if (!this.state.data || this.state.selectedPreviewIndex < 0 || this.state.selectedPreviewIndex >= this.state.data.previewData.length) {
            return;
        }

        const pdf = this.state.data.previewData[this.state.selectedPreviewIndex];
        let trNum = this.state.previewTRNumber;
        if (trNum.startsWith("TR")) {
            trNum = trNum.substring(2);
        }

        startDownloadBase64EncodedContent(`${this.state.previewPanelType} panel for TR${trNum}.pdf`, pdf.pdf, "application/pdf");
    }

    public render() {
        const previewData = this.state.data?.previewData[this.state.selectedPreviewIndex];

        return (
            <elix-dialog id="pd-previewpaneldialog" {...(!this.state.open ? { closed: true } : {})} aria-label="Preview Panel Dialog">
                <div className="elix-wrapper">
                    <header>
                        <div>
                            Preview Panel
                        </div>
                        <button type="button" className="pdButton elix-wrapper__close-btn" onClick={this.closeDialog}>X</button>
                    </header>
                    <section className={styles.previewContainer}>
                        {this.state.data ? (
                            <>
                                <div className={styles.toolbar}>
                                    <div className={styles.colourSpaceWarning}>
                                        <p>Note: Due to the colour space used in web browsers, some colours may not appear to be print accurate in this preview. Overprint will also not be displayed.</p>
                                        <p>Download and view this PDF in Adobe Acrobat or Adobe Illustrator to verify these particular artwork features.</p>
                                    </div>
                                    <button type="button" className="btn btn-primary" onClick={() => this.downloadCurrentPDF()} hidden={!previewData}>
                                        Download PDF
                                    </button>
                                </div>

                                <div className={styles.previewWrapper}>
                                    <div className={styles.previewPanelList} hidden={!previewData}>
                                        <ul>
                                            {this.state.data.previewData.map((p, i) => {
                                                const buttonClass = `pdButton ${this.state.selectedPreviewIndex === i ? "active" : ""}`;

                                                return (
                                                    <li key={i} role="button" className={buttonClass} onClick={e => this.setState({ selectedPreviewIndex: i })}>
                                                        {p.data.artworkType} panel {i + 1}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                    <PreviewDataContainer previewData={previewData} />
                                </div>
                            </>
                        ) : this.state.errorString ? (
                            <>
                                <h1>Error Fetching Preview</h1>
                                <p>{this.state.errorString}</p>
                            </>
                        ) : (
                            <h1>Loading...</h1>
                        )}
                    </section>
                </div>
            </elix-dialog>
        );
    }
}

interface PreviewDataContainerProps {
    previewData?: IPreviewData;
}

const PreviewDataContainer = ({ previewData }: PreviewDataContainerProps) => {
    if (!previewData) {
        return (
            <div className={styles.previewIframeContainer}>
                <div className={`alert alert-danger ${styles.previewErrors}`}>
                    <div><strong>No panel was generated.</strong></div>
                </div>
            </div>
        );
    }

    const iframeSrc = `data:application/pdf;base64,${previewData.pdf}`;
    const errors = previewData.data.errors ?? [];

    return (
        <div className={styles.previewIframeContainer}>
            <iframe className={styles.previewIframe} src={iframeSrc} />
            <div className={`alert alert-danger ${styles.previewErrors}`} hidden={errors.length <= 0}>
                <div><strong>This panel experienced warnings or errors while generating.</strong></div>
                <ol>
                    {errors.map((err, i) => (
                        <li key={err}>{err}</li>
                    ))}
                </ol>
            </div>
        </div>
    );
}
